<template>
  <div class="chip-color-picker">
    <!-- Mode toggle -->
    <v-btn-toggle v-model="mode" density="compact" mandatory variant="outlined" divided style="margin-bottom: 8px;">
      <v-btn value="simple" size="x-small">Simple</v-btn>
      <v-btn value="advanced" size="x-small">Advanced</v-btn>
    </v-btn-toggle>

    <!-- Simple mode: preset dropdown -->
    <v-select
      v-if="mode === 'simple'"
      v-model="simpleValue"
      :items="PRESET_COLORS"
      item-title="name"
      item-value="hex"
      density="compact"
      variant="outlined"
      hide-details
      @update:model-value="onSimpleSelect"
    >
      <template #prepend-inner>
        <div
          :style="{
            width: '18px', height: '18px', borderRadius: '3px',
            backgroundColor: simpleValue || modelValue,
            border: '1px solid #999',
            flexShrink: 0,
            marginRight: '4px'
          }"
        ></div>
      </template>
      <template #item="{ item, props: listItemProps }">
        <v-list-item v-bind="listItemProps">
          <template #prepend>
            <div
              :style="{
                width: '22px', height: '22px', borderRadius: '4px',
                backgroundColor: item.raw.hex,
                border: '1px solid #ccc',
                marginRight: '8px',
                flexShrink: 0
              }"
            ></div>
          </template>
        </v-list-item>
      </template>
    </v-select>

    <!-- Advanced mode: color wheel + hex + brightness -->
    <div v-if="mode === 'advanced'" style="display: flex; gap: 12px; align-items: flex-start; flex-wrap: wrap;">
      <canvas
        ref="wheelCanvas"
        :width="WHEEL_SIZE"
        :height="WHEEL_SIZE"
        class="color-wheel-canvas"
        @mousedown.prevent="startDrag"
        @mousemove="onMouseMove"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        @touchstart.prevent="onTouchStart"
        @touchmove.prevent="onTouchMove"
        @touchend="stopDrag"
      ></canvas>

      <div style="display: flex; flex-direction: column; gap: 8px; flex: 1; min-width: 110px;">
        <v-text-field
          v-model="hexInput"
          label="Hex Code"
          density="compact"
          variant="outlined"
          hide-details
          style="font-family: monospace;"
          @input="onHexInput"
        />

        <div style="font-size: 0.75rem; color: #666; margin-bottom: 2px;">Brightness</div>
        <div style="position: relative; height: 20px;">
          <div
            :style="{
              position: 'absolute',
              top: '6px', left: '0', right: '0',
              height: '8px',
              borderRadius: '4px',
              background: brightnessGradient,
              pointerEvents: 'none'
            }"
          ></div>
          <input
            type="range"
            :value="brightnessValue"
            min="0"
            max="1"
            step="0.01"
            class="brightness-range"
            @input="onBrightnessInput"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import { SHARED_PALETTE } from '@/utils/displayConfig';

const props = defineProps({
  modelValue: { type: String, default: '#FFFFFF' }
});

const emit = defineEmits(['update:modelValue']);

const WHEEL_SIZE = 160;
const WHEEL_R    = WHEEL_SIZE / 2;

const PRESET_COLORS = [
  { name: 'Red',    hex: '#CC0000' },
  { name: 'Blue',   hex: SHARED_PALETTE.blue },
  { name: 'Green',  hex: SHARED_PALETTE.green },
  { name: 'Yellow', hex: '#FFD600' },
  { name: 'Purple', hex: SHARED_PALETTE.purple },
  { name: 'Black',  hex: '#212121' },
  { name: 'White',  hex: '#FFFFFF' },
];

const mode            = ref('simple');
const wheelCanvas     = ref(null);
const hexInput        = ref(props.modelValue);
const brightnessValue = ref(1);
const isDragging      = ref(false);
const simpleValue     = ref(findPresetHex(props.modelValue));
const hsv             = ref({ h: 0, s: 0, v: 1 });

// ── color math ────────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  return [
    parseInt(hex.substr(0, 2), 16),
    parseInt(hex.substr(2, 2), 16),
    parseInt(hex.substr(4, 2), 16)
  ];
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase();
}

function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const v = max, d = max - min;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (max !== min) {
    if      (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else                h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s, v];
}

function hsvToRgb(h, s, v) {
  h = ((h % 360) + 360) % 360;
  const i = Math.floor(h / 60), f = h / 60 - i;
  const p = v * (1 - s), q = v * (1 - s * f), t = v * (1 - s * (1 - f));
  const cases = [[v,t,p],[q,v,p],[p,v,t],[p,q,v],[t,p,v],[v,p,q]];
  const [r, g, b] = cases[i % 6];
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function isValidHex(hex) {
  return /^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(hex);
}

function findPresetHex(hex) {
  return PRESET_COLORS.find(c => c.hex.toUpperCase() === hex.toUpperCase())?.hex ?? null;
}

function syncHsvFromHex(hex) {
  if (!isValidHex(hex)) return;
  const [r, g, b] = hexToRgb(hex);
  const [h, s, v] = rgbToHsv(r, g, b);
  hsv.value = { h, s, v };
  brightnessValue.value = v;
}

const brightnessGradient = computed(() => {
  const s = Math.max(hsv.value.s, 0.2);
  const [r, g, b] = hsvToRgb(hsv.value.h, s, 1);
  return `linear-gradient(to right, #000000, rgb(${r},${g},${b}))`;
});

// ── canvas ────────────────────────────────────────────────────────────────────

function drawWheel() {
  const canvas = wheelCanvas.value;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const cx = WHEEL_R, cy = WHEEL_R, R = WHEEL_R - 1;
  const v = brightnessValue.value;

  const imageData = ctx.createImageData(WHEEL_SIZE, WHEEL_SIZE);
  const data = imageData.data;
  for (let py = 0; py < WHEEL_SIZE; py++) {
    for (let px = 0; px < WHEEL_SIZE; px++) {
      const dx = px - cx, dy = py - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const i = (py * WHEEL_SIZE + px) * 4;
      if (dist > R) { data[i + 3] = 0; continue; }
      const hue = ((Math.atan2(dy, dx) * 180 / Math.PI) + 360) % 360;
      const [r, g, b] = hsvToRgb(hue, dist / R, v);
      data[i] = r; data[i + 1] = g; data[i + 2] = b; data[i + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);

  // Dot
  const angle = hsv.value.h * Math.PI / 180;
  const dotX = cx + hsv.value.s * R * Math.cos(angle);
  const dotY = cy + hsv.value.s * R * Math.sin(angle);
  ctx.beginPath(); ctx.arc(dotX, dotY, 7, 0, Math.PI * 2);
  ctx.strokeStyle = '#000'; ctx.lineWidth = 2.5; ctx.stroke();
  ctx.beginPath(); ctx.arc(dotX, dotY, 7, 0, Math.PI * 2);
  ctx.strokeStyle = '#FFF'; ctx.lineWidth = 1.5; ctx.stroke();
}

// ── events ────────────────────────────────────────────────────────────────────

function canvasPos(clientX, clientY) {
  const rect = wheelCanvas.value.getBoundingClientRect();
  return {
    x: (clientX - rect.left) * (WHEEL_SIZE / rect.width),
    y: (clientY - rect.top)  * (WHEEL_SIZE / rect.height)
  };
}

function pickAtPos(x, y) {
  const dx = x - WHEEL_R, dy = y - WHEEL_R, R = WHEEL_R - 1;
  hsv.value = {
    h: ((Math.atan2(dy, dx) * 180 / Math.PI) + 360) % 360,
    s: Math.min(Math.sqrt(dx * dx + dy * dy) / R, 1),
    v: brightnessValue.value
  };
  const [r, g, b] = hsvToRgb(hsv.value.h, hsv.value.s, hsv.value.v);
  const hex = rgbToHex(r, g, b);
  hexInput.value = hex;
  emit('update:modelValue', hex);
  drawWheel();
}

function startDrag(e) { isDragging.value = true; const p = canvasPos(e.clientX, e.clientY); pickAtPos(p.x, p.y); }
function onMouseMove(e) { if (isDragging.value) { const p = canvasPos(e.clientX, e.clientY); pickAtPos(p.x, p.y); } }
function stopDrag() { isDragging.value = false; }
function onTouchStart(e) { isDragging.value = true; const t = e.touches[0]; const p = canvasPos(t.clientX, t.clientY); pickAtPos(p.x, p.y); }
function onTouchMove(e) { const t = e.touches[0]; const p = canvasPos(t.clientX, t.clientY); pickAtPos(p.x, p.y); }

function onHexInput() {
  let val = hexInput.value.trim();
  if (!val.startsWith('#')) val = '#' + val;
  if (!isValidHex(val)) return;
  hexInput.value = val.toUpperCase();
  syncHsvFromHex(val);
  emit('update:modelValue', hexInput.value);
  drawWheel();
}

function onBrightnessInput(event) {
  const v = parseFloat(event.target.value);
  brightnessValue.value = v;
  hsv.value = { ...hsv.value, v };
  const [r, g, b] = hsvToRgb(hsv.value.h, hsv.value.s, v);
  const hex = rgbToHex(r, g, b);
  hexInput.value = hex;
  emit('update:modelValue', hex);
  drawWheel();
}

function onSimpleSelect(hex) {
  hexInput.value = hex;
  syncHsvFromHex(hex);
  emit('update:modelValue', hex);
}

// ── watchers ──────────────────────────────────────────────────────────────────

watch(() => props.modelValue, (hex) => {
  if (hex === hexInput.value) return;
  hexInput.value = hex;
  simpleValue.value = findPresetHex(hex);
  syncHsvFromHex(hex);
  if (mode.value === 'advanced') drawWheel();
});

watch(mode, async (val) => {
  if (val === 'advanced') {
    syncHsvFromHex(props.modelValue);
    await nextTick();
    drawWheel();
  }
});

onMounted(() => { syncHsvFromHex(props.modelValue); });
</script>

<style>
.chip-color-picker .color-wheel-canvas {
  cursor: crosshair;
  border-radius: 50%;
  display: block;
  flex-shrink: 0;
}

.chip-color-picker .brightness-range {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  outline: none;
}

.chip-color-picker .brightness-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #555555;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  margin-top: -5px;
}

.chip-color-picker .brightness-range::-webkit-slider-runnable-track {
  height: 8px;
  border-radius: 4px;
  background: transparent;
}

.chip-color-picker .brightness-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #555555;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.chip-color-picker .brightness-range::-moz-range-track {
  height: 8px;
  border-radius: 4px;
  background: transparent;
}
</style>
